'use client';

import type { ReactElement, ReactNode } from 'react';
import * as RadixPopover from '@radix-ui/react-popover';

import { classNames } from '@m1work/shared-utils';

import styles from './Popover.module.scss';

export interface PopoverProps {
    /** The element that opens the popover (or only positions it, with `asAnchor`). */
    trigger: ReactNode;
    children: ReactNode;
    /** Alignment against the trigger. @default 'end' */
    align?: 'start' | 'center' | 'end';
    /** Gap between the trigger and the popover, px. @default 8 */
    sideOffset?: number;
    className?: string;
    /** Open state (controlled). */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** The trigger only positions the popover and keeps the focus, e.g. a search field. @default false */
    asAnchor?: boolean;
    /** Stacking order of the popover; the app passes its own layer value. */
    zIndex?: number;
}

/**
 * Floating content attached to a trigger (Radix Popover): a menu, notifications, search results.
 *
 * @example
 * <Popover trigger={<IconButton aria-label={t('account')}><UserIcon /></IconButton>} zIndex={AppLayer.POPOVER}>
 *     <AccountMenu />
 * </Popover>
 */
export function Popover({
    trigger,
    children,
    align = 'end',
    sideOffset = 8,
    className,
    open,
    onOpenChange,
    asAnchor = false,
    zIndex,
}: PopoverProps): ReactElement {
    const TriggerPrimitive = asAnchor ? RadixPopover.Anchor : RadixPopover.Trigger;

    return (
        <RadixPopover.Root open={open} onOpenChange={onOpenChange}>
            <TriggerPrimitive asChild>{trigger}</TriggerPrimitive>
            <RadixPopover.Portal>
                <RadixPopover.Content
                    className={classNames(styles.content, className)}
                    align={align}
                    sideOffset={sideOffset}
                    style={{ zIndex }}
                    onOpenAutoFocus={asAnchor ? (e) => e.preventDefault() : undefined}
                >
                    {children}
                </RadixPopover.Content>
            </RadixPopover.Portal>
        </RadixPopover.Root>
    );
}
